import { useDispatch } from 'react-redux';
import { constant } from '@config/BaseSetting';
import useLocalStorage from '@hooks/useLocalStorage';
import { useEffect, useState } from 'react';
import { showFixedAlert } from '@features/application/applicationSlice';
import { useTranslation } from 'react-i18next';
import ClientRoutes from '@config/Routes/WebClientRoutes';
import { useInvoiceSeatAdjustmentCommandMutation } from '@services/WebApiService/InvoiceSeatAdjustment';
import useForm from './useForm';

export default function useAction({ onSuccessRequest, onFailedRequest }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const form = useForm();
  const [accessTokenSet] = useLocalStorage(constant.ACCESS_TOKEN_STORAGE);
  const [addHostedExchangeMutation, { isLoading }] = useInvoiceSeatAdjustmentCommandMutation();
  const [response, setResponse] = useState(null);
  const [disabled, setDisabled] = useState(false);

  const submitRequest = async () => {
    try {
      const period = form.periodAttributes.value;
      const periodValue = `${period.$y}-${(period.$M + 1).toString().padStart(2, '0')}`;

      const result = await addHostedExchangeMutation({
        accessToken: accessTokenSet,
        parameters: {
          'support-ticket': form.supportTicketAttributes.value,
          description: form.descriptionAttributes.value,
          'escalation-ticket': form.escalationTicketAttributes.value,
          'reseller-id': form.resellerIdAttributes.value,
          period: periodValue,
          'seat-payload': form.seatPayloadAttributes.value,
        },
      }).unwrap();
      setResponse(result);
    } catch (err) {
      setResponse({ error: err.data });
    }
  };

  const handleOnValidateData = (e) => {
    form.onValidateData(e);
  };

  useEffect(() => {
    if (form.isValid) {
      submitRequest();
    }
  }, [form.isValid]);

  const resetAll = () => {
    setResponse();
    form.resetForm();
  };

  useEffect(() => {
    if (response?.['status-code'] === 200) {
      if (response?.data?.serviceTypeCode) {
        const link = ClientRoutes.toolsRequestStatusPath({
          'service-type': response?.data?.serviceTypeCode,
        });
        dispatch(
          showFixedAlert({
            descriptionHtml: t('seat_adjustment.setup.success_submitted_with_link', {
              link,
            }),
          })
        );
      } else {
        dispatch(
          showFixedAlert({
            descriptionHtml: t('seat_adjustment.setup.success_submitted'),
          })
        );
      }
      if (onSuccessRequest) onSuccessRequest();
    } else if (response?.error) {
      onFailedRequest(response?.error?.message);
    }
    resetAll();
  }, [response]);

  return {
    form,
    isLoading,
    handleOnValidateData,
    resetAll,
    disabled,
  };
}
